import { PowerIntensityInputs } from '../../../components/workout/PowerIntensityInputs';
import { WorkoutZoneList, selectedWorkoutZone } from '../../../components/workout/WorkoutZoneList';
import { trainingZoneCustomName } from '../../../utils/trainingZones';
import { formatWorkoutNumber, workoutDisplayDigits } from '../../../utils/workoutNumberFormat';
import { NmLoader } from '../../../components/NmLoader';
import { ActionButton, ActionIconButton, ActionToggleGroup } from '../../../components/ui/ActionButton';
import '../../../styles/workout-editor-system.css';
import { SlideUpOverlay } from '../../../components/SlideUpOverlay';
import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { IoArrowBackOutline, IoChevronForwardOutline } from 'react-icons/io5';
import type { ApiStructBlock, ApiStructStep } from '../../../utils/workoutStructure';
import {
  isOpenDurationStep,
  openDurationStorageValue,
} from '../../../utils/workoutStructure';
import {
  ABSOLUTE_UNIT_LABELS,
  RANGE_MODE_PERCENT,
  RANGE_MODE_ZONE,
  RANGE_MODE_ABSOLUTE,
  RANGE_TOGGLE_LABELS,
  buildSingleTargetPercentOptions,
  buildLthrPercentOptions,
  buildAbsoluteTargetOptions,
  absoluteIndexFromStored,
  percentFromAbsoluteIndex,
  storedTargetsLookLikeAbsolute,
  formatBuilderIntensityLabel,
  getCategoryFromPrimaryMetric,
  getBuilderZoneRanges,
  hrPercentIndexFromStored,
  isAbsoluteTargetMode,
  singlePercentIndexFromStored,
  type PrimaryIntensityMetric,
} from '../../../utils/intensityMetricHelpers';
import type { PrimaryIntensityTargetOrRange } from '../../../utils/workoutBuilderSettings';
import { secToHMS } from '../../../utils/secToHMS';
import { WheelPicker, WheelPair, TimeWheelRow, HOUR_OPTIONS, MINSEC_OPTIONS, DIST_KM_OPTIONS, DIST_FRAC_OPTIONS } from '../../../components/workout/WheelPicker';

const GARMIN_STEP_TYPES = ['warmup', 'active', 'interval', 'rest', 'recovery', 'cooldown', 'other'];
const STEP_TYPE_LABELS_FA = ['گرم‌کردن', 'فعال', 'اینتروال', 'استراحت', 'ریکاوری', 'سردکردن', 'سایر'];

function formatStepLengthLabel(length?: { value?: number | string; unit?: string }): string {
  if (!length) return '—';
  const unit = length.unit ?? 'second';
  const val = Number(length.value ?? 0);
  if (unit === 'second' || unit.startsWith('second')) return secToHMS(val);
  if (unit === 'meter') return `${val} m`;
  return String(val);
}

export interface WorkoutStepConfigProps {
  running?: boolean;
  itemModal: boolean;
  setItemModal: (v: boolean) => void;
  loopModal: boolean;
  setLoopModal: (v: boolean) => void;
  stepTypeModal: boolean;
  setStepTypeModal: (v: boolean) => void;
  durationModal: boolean;
  setDurationModal: (v: boolean) => void;
  secondaryModal: boolean;
  setSecondaryModal: (v: boolean) => void;
  intensityTarget: boolean;
  setIntensityTarget: (v: boolean) => void;
  dragData: ApiStructBlock[];
  setDragData: (data: ApiStructBlock[]) => void;
  tempo: ApiStructBlock[];
  setTempo: (data: ApiStructBlock[]) => void;
  itemData: { parentIndex: number; index: number };
  powerMode: number;
  setPowerMode: (m: number) => void;
  loadingZones: boolean;
  zoneSettings: Record<string, unknown> | null;
  fetchZones: () => void;
  ftpValue: number;
  FtpArray: string[];
  ltHrValue: number;
  LthrArray: string[];
  maxHrValue?: number;
  thresholdSpeedMps?: number;
  selectedLoop: string | number | null;
  primaryIntensityMetric: PrimaryIntensityMetric;
  primaryIntensityTargetOrRange: PrimaryIntensityTargetOrRange;
}

function ConfigRow({
  label,
  value,
  badge,
  onPress,
}: {
  label: string;
  value?: string;
  badge?: string;
  onPress?: () => void;
}) {
  const inner = (
    <div className="wb-step-config__row">
      <span className="wb-step-config__row-label">{label}</span>
      <div className="wb-step-config__row-right">
        {badge && <span className="wb-step-config__badge">{badge}</span>}
        {value && <span className="wb-step-config__row-value">{value}</span>}
        {onPress && <IoChevronForwardOutline className="wb-step-config__chevron" aria-hidden />}
      </div>
    </div>
  );
  if (onPress) {
    return (
      <button type="button" className="wb-step-config__row-btn" onClick={onPress}>
        {inner}
      </button>
    );
  }
  return inner;
}

function ToggleRow({
  label,
  value,
  active,
  onPress,
}: {
  label: string;
  value?: string;
  active: boolean;
  onPress: () => void;
}) {
  return (
    <button type="button" className="wb-step-config__row-btn" onClick={onPress} aria-pressed={active}>
      <div className="wb-step-config__row">
        <span className="wb-step-config__row-label">{label}</span>
        <div className="wb-step-config__row-right">
          {value && <span className="wb-step-config__row-value">{value}</span>}
          <div className={`wb-step-config__toggle${active ? ' wb-step-config__toggle--on' : ''}`}>
            <div className="wb-step-config__toggle-thumb" />
          </div>
        </div>
      </div>
    </button>
  );
}

function DialogCard({ title, onCancel, onConfirm, children, confirmDisabled = false }: {
  title: string; onCancel: () => void; onConfirm: () => void; children: React.ReactNode; confirmDisabled?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="wb-step-dialog">
      <h3 className="wb-step-dialog__title">{title}</h3>
      <div className="wb-step-dialog__body">{children}</div>
      <div className="wb-step-dialog__foot">
        <ActionButton type="button" className="wb-step-dialog__btn wb-step-dialog__btn--cancel" onClick={onCancel}>{t('workoutBuilder.cancel')}</ActionButton>
        <ActionButton type="button" className="wb-step-dialog__btn wb-step-dialog__btn--confirm" onClick={onConfirm} variant="primary" disabled={confirmDisabled}>{t('workoutBuilder.save')}</ActionButton>
      </div>
    </div>
  );
}

function SegmentPill({ options, value, onChange }: {
  options: { label: string; value: string }[]; value: string; onChange: (value: string) => void;
}) {
  return <ActionToggleGroup className="wb-step-seg" label={options.map(option => option.label).join(' / ')} options={options} value={value} onChange={onChange} />;
}

/** Fullscreen step editor + sub-modals — mirrors Ai-Tempo WorkoutStepConfig. */
export function WorkoutStepConfig({
  running = false,
  itemModal,
  setItemModal,
  loopModal,
  setLoopModal,
  stepTypeModal,
  setStepTypeModal,
  durationModal,
  setDurationModal,
  secondaryModal,
  setSecondaryModal,
  intensityTarget,
  setIntensityTarget,
  dragData,
  setDragData,
  tempo,
  setTempo,
  itemData,
  powerMode,
  setPowerMode,
  loadingZones,
  zoneSettings,
  fetchZones,
  ftpValue,
  ltHrValue,
  LthrArray,
  maxHrValue = 0,
  thresholdSpeedMps = 0,
  selectedLoop,
  primaryIntensityMetric,
  primaryIntensityTargetOrRange,
}: WorkoutStepConfigProps) {
  const { t, i18n } = useTranslation();
  const [powerInputValid, setPowerInputValid] = React.useState(true);
  const zoneNumberFormat = { format: (value: number) => formatWorkoutNumber(value, i18n.resolvedLanguage || i18n.language, { useGrouping: false, maximumFractionDigits: 0 }) };
  const localizeZoneLabel = (label: string) => workoutDisplayDigits(label, i18n.resolvedLanguage || i18n.language);
  const zoneName = (zone: { label: string; number: number }) => localizeZoneLabel(trainingZoneCustomName(zone.label) ?? t('workoutBuilder.zoneRangeNumber', { number: zoneNumberFormat.format(zone.number) }));
  const intensityCategory = getCategoryFromPrimaryMetric(primaryIntensityMetric);
  const rangeToggleLabels = intensityCategory ? RANGE_TOGGLE_LABELS[intensityCategory] : null;
  const absoluteMode = isAbsoluteTargetMode(primaryIntensityTargetOrRange);
  const intensityThresholds = {
    ftp: ftpValue,
    ltHr: ltHrValue,
    maxHr: maxHrValue,
    thresholdSpeedMps,
  };

  const step = dragData?.[itemData.parentIndex]?.steps?.[itemData.index] as ApiStructStep | undefined;
  const tempoStep = tempo?.[itemData.parentIndex]?.steps?.[itemData.index] as ApiStructStep | undefined;
  const availableZones = getBuilderZoneRanges(zoneSettings, primaryIntensityMetric, intensityThresholds);
  const chosenZone = selectedWorkoutZone(availableZones, Number(tempoStep?.targets?.[0]?.minValue), Number(tempoStep?.targets?.[0]?.maxValue));

  const tempoVal = Number(tempoStep?.length?.value ?? 0);
  const tempoDistanceKm = tempoVal / 1000;
  /** Live unit while duration modal is open — edits go to `tempo`, not `dragData`. */
  const modalLengthUnit: 'second' | 'meter' = (tempoStep?.length?.unit as 'second' | 'meter') ?? 'second';
  const cadenceEnabled = (step?.targets?.length ?? 0) > 1;

  const patchStep = (patch: Record<string, unknown>) => {
    const a = [...dragData];
    const b = [...(a[itemData.parentIndex].steps ?? [])];
    b[itemData.index] = { ...b[itemData.index], ...patch } as ApiStructStep;
    a[itemData.parentIndex] = { ...a[itemData.parentIndex], steps: b };
    setDragData([...a]);
  };

  const patchTempoStep = (patch: Record<string, unknown>) => {
    const a = [...tempo];
    const b = [...(a[itemData.parentIndex].steps ?? [])];
    b[itemData.index] = { ...b[itemData.index], ...patch } as ApiStructStep;
    a[itemData.parentIndex] = { ...a[itemData.parentIndex], steps: b };
    setTempo([...a]);
  };

  const patchTempoTargets = (ti: number, patch: Record<string, unknown>) => {
    const a = [...tempo];
    const b = [...(a[itemData.parentIndex].steps ?? [])];
    const c = [...(b[itemData.index].targets ?? [])];
    c[ti] = { ...c[ti], ...patch };
    b[itemData.index] = { ...b[itemData.index], targets: c };
    a[itemData.parentIndex] = { ...a[itemData.parentIndex], steps: b };
    setTempo([...a]);
  };

  const intensityRangeLabel = (() => {
    const tgt = step?.targets?.[0];
    if (tgt?.minValue == null && tgt?.maxValue == null) return '—';
    return formatBuilderIntensityLabel(
      tgt?.minValue,
      tgt?.maxValue,
      primaryIntensityMetric,
      intensityThresholds,
      primaryIntensityTargetOrRange,
    );
  })();

  const tempoStepOpen = isOpenDurationStep(tempoStep?.openDuration);

  const durationLabel = (() => {
    if (isOpenDurationStep(step?.openDuration)) return t('workoutBuilder.durationModeLap');
    return formatStepLengthLabel(step?.length);
  })();

  const portalRoot = typeof document !== 'undefined' ? document.body : null;
  if (!portalRoot) return null;

  const anyOpen =
    itemModal || loopModal || stepTypeModal || durationModal || secondaryModal || intensityTarget;
  if (!anyOpen) return null;

  return createPortal(
    <>
      {itemModal && (
        <SlideUpOverlay open={itemModal} onClose={() => setItemModal(false)} variant="sheet" zIndex={15000} panelClassName="wb-step-config-screen wb-step-config-screen--sheet" aria-label={t('workoutBuilder.editStep')}>
          <div className="wb-step-config-screen__nav">
            <ActionIconButton
              type="button"
              className="wb-step-config-screen__close"
              onClick={() => setItemModal(false)}
              label={t('workoutBuilder.glass.closeEditor')}
              icon={<IoArrowBackOutline aria-hidden />}
            />
            <div className="wb-step-config-screen__nav-copy">
              <strong>{t('workoutBuilder.editStep')}</strong>
              <span>{t('workoutBuilder.changesSaved')}</span>
            </div>
            <ActionButton
              type="button"
              className="wb-step-config-screen__done"
              onClick={() => setItemModal(false)}
              variant="primary"
            >{t('workoutBuilder.done')}</ActionButton>
          </div>
          <div className="wb-step-config-screen__hero">
            <input
              className="wb-step-config-screen__name"
              value={step?.name ?? ''}
              onChange={e => patchStep({ name: e.target.value })}
              placeholder={t('workoutBuilder.stepName')}
              aria-label={t('workoutBuilder.stepName')}
              autoComplete="off"
            />
          </div>
          <div className="wb-step-config-screen__list">
            <ConfigRow
              label={t('workoutBuilder.stepType')}
              value={step?.intensityClass ?? '—'}
              onPress={() => { setTempo(JSON.parse(JSON.stringify(dragData))); setStepTypeModal(true); }}
            />
            <ConfigRow
              label={t('workoutBuilder.intensityTarget')}
              value={intensityRangeLabel}
              badge={
                absoluteMode
                  ? ABSOLUTE_UNIT_LABELS[primaryIntensityMetric]
                  : undefined
              }
              onPress={() => {
                if (intensityCategory && !absoluteMode) {
                  const t0 = step?.targets?.[0];
                  const min = Number(t0?.minValue ?? 0);
                  const max = Number(t0?.maxValue ?? min);
                  if (storedTargetsLookLikeAbsolute(min, max, primaryIntensityMetric)) {
                    setPowerMode(RANGE_MODE_ABSOLUTE);
                  } else {
                    setPowerMode(RANGE_MODE_PERCENT);
                  }
                }
                setTempo(JSON.parse(JSON.stringify(dragData)));
                setIntensityTarget(true);
                if (absoluteMode && intensityCategory === 'power') setPowerMode(RANGE_MODE_PERCENT);
                if (!zoneSettings && !loadingZones) fetchZones();
              }}
            />
            <ConfigRow
              label={t('workoutBuilder.stepDuration')}
              value={durationLabel}
              onPress={() => { setTempo(JSON.parse(JSON.stringify(dragData))); setDurationModal(true); }}
            />
            <ToggleRow
              label={t(running ? 'runningMetrics.cadence' : 'workoutBuilder.cadence')}
              value={
                cadenceEnabled
                  ? `${step?.targets?.[1]?.minValue ?? 0} – ${step?.targets?.[1]?.maxValue ?? 0} ${running ? t('runningMetrics.cadenceUnit') : 'rpm'}`
                  : undefined
              }
              active={cadenceEnabled}
              onPress={() => {
                const a = [...dragData];
                const b = [...(a[itemData.parentIndex].steps ?? [])];
                let c = [...(b[itemData.index]?.targets ?? [])];
                c =
                  (c[1] as { unit?: string })?.unit === 'roundOrStridePerMinute'
                    ? [c[0]]
                    : [...c, { minValue: 90, maxValue: 100, unit: 'roundOrStridePerMinute' }];
                b[itemData.index] = { ...b[itemData.index], targets: c };
                a[itemData.parentIndex] = { ...a[itemData.parentIndex], steps: b };
                setDragData([...a]);
              }}
            />
            {cadenceEnabled && (
              <ConfigRow
                label={running ? `${t('runningMetrics.cadence')} (${t('runningMetrics.cadenceUnit')})` : t('workoutBuilder.cadenceRange')}
                value={`${step?.targets?.[1]?.minValue ?? 0} – ${step?.targets?.[1]?.maxValue ?? 0} ${running ? t('runningMetrics.cadenceUnit') : 'rpm'}`}
                onPress={() => { setTempo([...dragData]); setSecondaryModal(true); }}
              />
            )}
          </div>
        </SlideUpOverlay>
      )}

      {loopModal && (
        <div className="wb-step-modal-backdrop">
          <DialogCard
            title={t('workoutBuilder.repeatCount')}
            onCancel={() => { setTempo(dragData); setLoopModal(false); }}
            onConfirm={() => { setDragData(tempo); setLoopModal(false); }}>
            <WheelPicker
              options={[...Array(40).keys()].map(i => String(i))}
              selected={Number(tempo?.[Number(selectedLoop ?? 0)]?.length?.value ?? 3)}
              onChange={index => {
                const a = [...tempo];
                const idx = Number(selectedLoop ?? 0);
                a[idx] = {
                  ...a[idx],
                  length: { ...a[idx]?.length, value: index, unit: a[idx]?.length?.unit ?? 'repetition' },
                };
                setTempo([...a]);
              }}
            />
          </DialogCard>
        </div>
      )}

      {stepTypeModal && (
        <div className="wb-step-modal-backdrop">
          <DialogCard
            title={t('workoutBuilder.stepTypeTitle')}
            onCancel={() => { setTempo(dragData); setStepTypeModal(false); }}
            onConfirm={() => { setDragData(tempo); setStepTypeModal(false); }}>
            <WheelPicker
              options={STEP_TYPE_LABELS_FA}
              selected={(() => {
                const cur = tempo?.[itemData.parentIndex]?.steps?.[itemData.index]?.intensityClass;
                const gi = GARMIN_STEP_TYPES.indexOf(cur ?? '');
                return gi >= 0 ? gi : 0;
              })()}
              onChange={index => {
                patchTempoStep({ intensityClass: GARMIN_STEP_TYPES[index] });
              }}
            />
          </DialogCard>
        </div>
      )}

      {durationModal && (
        <div className="wb-step-modal-backdrop">
          <DialogCard
            title={modalLengthUnit === 'second' ? t('workoutBuilder.stepDuration') : t('workoutBuilder.stepDistance')}
            onCancel={() => { setTempo(dragData); setDurationModal(false); }}
            onConfirm={() => { setDragData(tempo); setDurationModal(false); }}>
            <SegmentPill
              options={[
                { label: t('workoutBuilder.durationTime'), value: 'second' },
                { label: t('workoutBuilder.durationDistance'), value: 'meter' },
              ]}
              value={modalLengthUnit}
              onChange={v => {
                const prev = tempoStep?.length;
                const prevUnit = prev?.unit ?? 'second';
                let nextValue = Number(prev?.value ?? 0);
                if (v === 'meter' && prevUnit !== 'meter') {
                  nextValue = 5000;
                } else if (v === 'second' && prevUnit !== 'second') {
                  nextValue = 600;
                }
                patchTempoStep({
                  length: { unit: v, value: nextValue },
                  openDuration: openDurationStorageValue(false),
                });
              }}
            />
            <SegmentPill
              options={[
                { label: t('workoutBuilder.durationModeFixed'), value: 'fixed' },
                { label: t('workoutBuilder.durationModeLap'), value: 'open' },
              ]}
              value={tempoStepOpen ? 'open' : 'fixed'}
              onChange={v =>
                patchTempoStep({
                  openDuration: openDurationStorageValue(v === 'open'),
                })
              }
            />
            {!tempoStepOpen && modalLengthUnit === 'second' && (
              <TimeWheelRow
                hours={
                  <WheelPicker
                    options={HOUR_OPTIONS}
                    selected={Math.min(Math.floor(tempoVal / 3600), 23)}
                    onChange={h => {
                      const m = Math.floor((tempoVal % 3600) / 60);
                      const sec = tempoVal % 60;
                      patchTempoStep({ length: { unit: 'second', value: h * 3600 + m * 60 + sec } });
                    }}
                  />
                }
                minutes={
                  <WheelPicker
                    options={MINSEC_OPTIONS}
                    selected={Math.floor((tempoVal % 3600) / 60)}
                    onChange={m => {
                      const h = Math.floor(tempoVal / 3600);
                      const sec = tempoVal % 60;
                      patchTempoStep({ length: { unit: 'second', value: h * 3600 + m * 60 + sec } });
                    }}
                  />
                }
                seconds={
                  <WheelPicker
                    options={MINSEC_OPTIONS}
                    selected={tempoVal % 60}
                    onChange={sec => {
                      const h = Math.floor(tempoVal / 3600);
                      const m = Math.floor((tempoVal % 3600) / 60);
                      patchTempoStep({ length: { unit: 'second', value: h * 3600 + m * 60 + sec } });
                    }}
                  />
                }
              />
            )}
            {!tempoStepOpen && modalLengthUnit === 'meter' && (
              <WheelPair
                left={
                  <WheelPicker
                    options={DIST_KM_OPTIONS}
                    selected={Math.min(Math.floor(tempoDistanceKm), DIST_KM_OPTIONS.length - 1)}
                    onChange={index => {
                      const frac = tempoDistanceKm - Math.floor(tempoDistanceKm);
                      patchTempoStep({ length: { unit: 'meter', value: Math.round((index + frac) * 1000) } });
                    }}
                  />
                }
                right={
                  <WheelPicker
                    options={DIST_FRAC_OPTIONS}
                    selected={Math.round(tempoVal) % 1000}
                    onChange={index => {
                      patchTempoStep({ length: { unit: 'meter', value: Math.floor(tempoDistanceKm) * 1000 + index } });
                    }}
                  />
                }
              />
            )}
          </DialogCard>
        </div>
      )}

      {secondaryModal && (
        <div className="wb-step-modal-backdrop">
          <DialogCard
            title={running ? `${t('runningMetrics.cadence')} (${t('runningMetrics.cadenceUnit')})` : t('workoutBuilder.cadenceTarget')}
            onCancel={() => { setTempo(dragData); setSecondaryModal(false); }}
            onConfirm={() => { setDragData(tempo); setSecondaryModal(false); }}>
            <WheelPair
              left={
                <WheelPicker
                  options={[...Array(200).keys()].map(i => String(i))}
                  selected={Math.min(Number(tempoStep?.targets?.[1]?.minValue ?? 0), 199)}
                  onChange={index => patchTempoTargets(1, { minValue: index })}
                />
              }
              right={
                <WheelPicker
                  options={[...Array(200).keys()].map(i => String(i))}
                  selected={Math.min(Number(tempoStep?.targets?.[1]?.maxValue ?? 0), 199)}
                  onChange={index => patchTempoTargets(1, { maxValue: index })}
                />
              }
            />
          </DialogCard>
        </div>
      )}

      {intensityTarget && (
        <div className="wb-step-modal-backdrop">
          <DialogCard
            title={
              absoluteMode
                ? t('workoutBuilder.intensityAbsoluteTitle')
                : t('workoutBuilder.intensityRangeTitle')
            }
            confirmDisabled={!powerInputValid || (!absoluteMode && powerMode === RANGE_MODE_ZONE && chosenZone < 0)}
            onCancel={() => { setTempo(dragData); setIntensityTarget(false); }}
            onConfirm={() => {
              let confirmed = tempo;
              if (!absoluteMode && powerMode === RANGE_MODE_ZONE && intensityCategory) {
                const zones = getBuilderZoneRanges(zoneSettings, primaryIntensityMetric, intensityThresholds);
                const index = selectedWorkoutZone(zones, Number(tempoStep?.targets?.[0]?.minValue), Number(tempoStep?.targets?.[0]?.maxValue));
                const zone = zones[index];
                if (!zone) return;
                confirmed = tempo.map((block, blockIndex) => blockIndex !== itemData.parentIndex ? block : {
                  ...block, steps: block.steps?.map((current, stepIndex) => stepIndex !== itemData.index ? current : {
                    ...current, targets: [{ ...current.targets?.[0], minValue: zone.minimum, maxValue: zone.maximum }, ...(current.targets?.slice(1) ?? [])],
                  }),
                });
              }
              setDragData(confirmed);
              setIntensityTarget(false);
            }}>
            {loadingZones ? (
              <p className="wb-step-dialog__loading"><NmLoader size="sm" inline label={t('common.loading')} /></p>
            ) : !intensityCategory ? (
              <p className="wb-step-dialog__empty">{t('workoutBuilder.selectIntensityInSetup')}</p>
            ) : (
              <>
                {(!absoluteMode || intensityCategory === 'power') && rangeToggleLabels && (() => {
                  const zones = getBuilderZoneRanges(zoneSettings, primaryIntensityMetric, intensityThresholds);
                  const minVal = tempoStep?.targets?.[0]?.minValue;
                  const activeZoneIdx = zones?.findIndex(z => z?.minimum === minVal) ?? -1;
                  const activeZone = activeZoneIdx >= 0 ? zones[activeZoneIdx] : null;
                  const zonePillLabel =
                    powerMode === RANGE_MODE_ZONE
                      ? (activeZone ? zoneName(activeZone) : t('workoutBuilder.zoneRanges'))
                      : t('workoutBuilder.zoneRanges');
                  return (
                    <SegmentPill
                      options={[
                        { label: rangeToggleLabels.percent, value: String(RANGE_MODE_PERCENT) },
                        ...(!absoluteMode ? [{ label: zonePillLabel, value: String(RANGE_MODE_ZONE) }] : []),
                        { label: rangeToggleLabels.absolute, value: String(RANGE_MODE_ABSOLUTE) },
                      ]}
                      value={String(powerMode)}
                      onChange={v => setPowerMode(Number(v))}
                    />
                  );
                })()}

                {absoluteMode && intensityCategory && intensityCategory !== 'power' && (
                  <WheelPicker
                    variant="rich"
                    className="wb-wheel--full"
                    options={buildSingleTargetPercentOptions(primaryIntensityMetric, intensityThresholds)}
                    selected={singlePercentIndexFromStored(
                      Number(tempoStep?.targets?.[0]?.minValue ?? tempoStep?.targets?.[0]?.maxValue),
                      primaryIntensityMetric,
                      intensityThresholds,
                    )}
                    onChange={index =>
                      patchTempoTargets(0, { minValue: index, maxValue: index })
                    }
                  />
                )}

                {intensityCategory === 'power' && (absoluteMode || powerMode !== RANGE_MODE_ZONE) && (
                  <PowerIntensityInputs key={`${powerMode}-${absoluteMode}`}
                    minValue={Number(tempoStep?.targets?.[0]?.minValue ?? 0)}
                    maxValue={Number(tempoStep?.targets?.[0]?.maxValue ?? tempoStep?.targets?.[0]?.minValue ?? 0)}
                    ftp={ftpValue} unit={powerMode === RANGE_MODE_ABSOLUTE ? 'watts' : 'percent'} single={absoluteMode}
                    onChange={values => patchTempoTargets(0, values)} onValidityChange={setPowerInputValid} />
                )}

                {!absoluteMode && intensityCategory === 'hr' && powerMode === RANGE_MODE_PERCENT && (
                  <WheelPair
                    className="wb-wheel-pair--range"
                    left={
                      <WheelPicker
                        variant="rich"
                        options={LthrArray.length ? LthrArray : buildLthrPercentOptions(ltHrValue)}
                        selected={hrPercentIndexFromStored(
                          Number(tempoStep?.targets?.[0]?.minValue),
                          ltHrValue,
                        )}
                        onChange={index => patchTempoTargets(0, { minValue: index })}
                      />
                    }
                    right={
                      <WheelPicker
                        variant="rich"
                        options={LthrArray.length ? LthrArray : buildLthrPercentOptions(ltHrValue)}
                        selected={hrPercentIndexFromStored(
                          Number(tempoStep?.targets?.[0]?.maxValue),
                          ltHrValue,
                        )}
                        onChange={index => patchTempoTargets(0, { maxValue: index })}
                      />
                    }
                  />
                )}

                {!absoluteMode && intensityCategory === 'speed' && powerMode === RANGE_MODE_PERCENT && (
                  <WheelPair
                    className="wb-wheel-pair--range"
                    left={
                      <WheelPicker
                        variant="rich"
                        options={[...Array(100).keys()].map(i => `${i}%`)}
                        selected={Math.min(Number(tempoStep?.targets?.[0]?.minValue ?? 0), 99)}
                        onChange={index => patchTempoTargets(0, { minValue: index })}
                      />
                    }
                    right={
                      <WheelPicker
                        variant="rich"
                        options={[...Array(100).keys()].map(i => `${i}%`)}
                        selected={Math.min(Number(tempoStep?.targets?.[0]?.maxValue ?? 0), 99)}
                        onChange={index => patchTempoTargets(0, { maxValue: index })}
                      />
                    }
                  />
                )}

                {!absoluteMode && powerMode === RANGE_MODE_ABSOLUTE && intensityCategory && intensityCategory !== 'power' && (
                  <WheelPair
                    className="wb-wheel-pair--range"
                    left={
                      <WheelPicker
                        variant="rich"
                        options={buildAbsoluteTargetOptions(primaryIntensityMetric, intensityThresholds)}
                        selected={absoluteIndexFromStored(
                          Number(tempoStep?.targets?.[0]?.minValue),
                          primaryIntensityMetric,
                          intensityThresholds,
                        )}
                        onChange={index =>
                          patchTempoTargets(0, {
                            minValue: percentFromAbsoluteIndex(
                              index,
                              primaryIntensityMetric,
                              intensityThresholds,
                            ),
                          })
                        }
                      />
                    }
                    right={
                      <WheelPicker
                        variant="rich"
                        options={buildAbsoluteTargetOptions(primaryIntensityMetric, intensityThresholds)}
                        selected={absoluteIndexFromStored(
                          Number(tempoStep?.targets?.[0]?.maxValue),
                          primaryIntensityMetric,
                          intensityThresholds,
                        )}
                        onChange={index =>
                          patchTempoTargets(0, {
                            maxValue: percentFromAbsoluteIndex(
                              index,
                              primaryIntensityMetric,
                              intensityThresholds,
                            ),
                          })
                        }
                      />
                    }
                  />
                )}

                {!absoluteMode && powerMode === RANGE_MODE_ZONE && intensityCategory && (
                  <WorkoutZoneList zones={availableZones}
                    minValue={Number(tempoStep?.targets?.[0]?.minValue)} maxValue={Number(tempoStep?.targets?.[0]?.maxValue)}
                    onChange={values => patchTempoTargets(0, values)} />
                )}
              </>
            )}
          </DialogCard>
        </div>
      )}
    </>,
    portalRoot,
  );
}
